import { useEffect, useId, useState } from 'react';

interface Props {
  label: string;
  value: number;
  onCommit: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  hideLabel?: boolean;
}

/**
 * A number input that keeps its own text while focused.
 *
 * Without this, clearing the field to retype sends `0` to the model mid-edit
 * and the whole diagram jumps to zero between keystrokes. Local text wins while
 * focused; the model wins whenever the value changes from elsewhere.
 *
 * The unit sits outside the <label> so the field's accessible name is exactly
 * its label — wrapping it in would name the control "Reach%".
 */
export function NumberField({
  label,
  value,
  onCommit,
  min = 0,
  max,
  step = 1,
  suffix,
  hideLabel,
}: Props) {
  const id = useId();
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  return (
    <div className="number-field">
      <label className={hideLabel ? 'sr-only' : 'number-field-label'} htmlFor={id}>
        {label}
      </label>
      <span className="number-field-control">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={text}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setText(String(value));
          }}
          onChange={(event) => {
            setText(event.target.value);
            const next = Number.parseFloat(event.target.value);
            if (Number.isFinite(next)) onCommit(next);
          }}
        />
        {suffix && (
          <span className="number-field-suffix" aria-hidden="true">
            {suffix}
          </span>
        )}
      </span>
    </div>
  );
}
